import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthSessionService } from '../services/auth-session.service';
import { AuthAttemptService } from '../services/auth-attempt.service';
import { PostLoginRedirectService } from '../services/post-login-redirect.service';

/**
 * Decides where the IdP redirect lands.
 *
 * By the time this runs, `checkAuth()` has already processed the `?code=` — it
 * happens in the bootstrap initializer (`provideAuthSession`), which Angular
 * completes before the router's first navigation. So this guard only has to
 * read the outcome and hand back a `UrlTree`.
 *
 * It is a guard rather than a component on purpose. The previous
 * implementation navigated from a component constructor, which made it the
 * third navigation competing for the same callback (the library also navigates
 * to `postLoginRoute` on success and to `unauthorizedRoute` on failure), and
 * the winner was timing-dependent. Returning a `UrlTree` from the guard is a
 * single, ordered decision with nothing to render in between.
 */
export const authCallbackGuard: CanActivateFn = () => {
  const session = inject(AuthSessionService);
  const attempts = inject(AuthAttemptService);
  const redirect = inject(PostLoginRedirectService);
  const router = inject(Router);

  if (session.result()?.isAuthenticated) {
    attempts.reset();

    return router.parseUrl(redirect.consume() ?? '/');
  }

  // Authentication failed. Retry once — a hiccup is worth one more round trip
  // — but stop after that, or the guarded route tree will keep calling
  // `login()` and the IdP will keep answering from its session cookie.
  if (attempts.recordFailure()) {
    return router.parseUrl('/unauthorized');
  }

  return router.parseUrl('/');
};
